import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef, useRef } from 'react';
import { MODES } from './modeConfig';
import { useAuth } from './AuthContext';
import { authFetch } from './api';

const API_URL = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 30;

// ── Helpers ──────────────────────────────────────────────────────

function getModeConfig(modeId) {
  return MODES.find(m => m.id === modeId) || MODES[0];
}

function readLocalSessions(key) {
  try { return JSON.parse(localStorage.getItem(key) || '[]'); }
  catch { return []; }
}

function groupSessionsByDate(sessions) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const prev7 = new Date(today);
  prev7.setDate(today.getDate() - 7);
  const prev30 = new Date(today);
  prev30.setDate(today.getDate() - 30);

  const groups = [
    { label: 'Today', sessions: [] },
    { label: 'Yesterday', sessions: [] },
    { label: 'Previous 7 days', sessions: [] },
    { label: 'Previous 30 days', sessions: [] },
    { label: 'Older', sessions: [] },
  ];

  for (const session of sessions) {
    const d = new Date(session.updatedAt || session.createdAt);
    if (d >= today) groups[0].sessions.push(session);
    else if (d >= yesterday) groups[1].sessions.push(session);
    else if (d >= prev7) groups[2].sessions.push(session);
    else if (d >= prev30) groups[3].sessions.push(session);
    else groups[4].sessions.push(session);
  }

  return groups.filter(g => g.sessions.length > 0);
}

// ── Component ────────────────────────────────────────────────────

const Sidebar = forwardRef(function Sidebar(
  { activeSessionId, onOpenSession, onNewSession, isCollapsed, onToggleCollapse, onOpenSettings, onOpenKnowledge, onOpenForest },
  ref
) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedForests, setExpandedForests] = useState(new Set());
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [dragOverId, setDragOverId] = useState(null);
  const dragSessionRef = useRef(null);
  const { user: authUser, logout: authLogout } = useAuth();
  const sentinelRef = useRef(null);
  const sessionsContainerRef = useRef(null);

  // Fetch sessions — initial load or load more (cursor-based)
  const fetchSessions = useCallback(async (before = null) => {
    if (before) setLoadingMore(true);
    else setLoading(true);

    try {
      let serverSessions = [];
      try {
        const url = `${API_URL}/api/sessions?limit=${PAGE_SIZE}${before ? `&before=${encodeURIComponent(before)}` : ''}`;
        const res = await authFetch(url);
        if (res.ok) serverSessions = await res.json();
      } catch { /* server unavailable */ }

      // Normalize: first line only, strip markdown, collapse whitespace
      const normalizeIdea = (text) =>
        (text || '').split('\n')[0].replace(/^#+\s*/, '').replace(/\s+/g, ' ').toLowerCase().trim().slice(0, 40);

      // Only include local sessions on initial load
      let localSessions = [];
      if (!before) {
        const ideaSessions = readLocalSessions('IDEA_CANVAS_SESSIONS').map(s => ({
          ...s,
          idea: s.label || s.idea || '',
          mode: s.mode || 'idea',
          source: 'local',
          updatedAt: s.timestamp ? new Date(s.timestamp).toISOString() : s.id,
        }));
        const codeSessions = readLocalSessions('CODEBASE_CANVAS_SESSIONS').map(s => ({
          ...s,
          idea: s.label || s.idea || '',
          mode: s.mode || 'codebase',
          source: 'local',
          updatedAt: s.timestamp ? new Date(s.timestamp).toISOString() : s.id,
        }));

        const serverIdeas = new Set(serverSessions.map(s => normalizeIdea(s.idea)));
        const uniqueLocal = [...ideaSessions, ...codeSessions].filter(
          s => !serverIdeas.has(normalizeIdea(s.idea))
        );
        const seenIdeas = new Set();
        localSessions = uniqueLocal.filter(s => {
          const key = normalizeIdea(s.idea);
          if (!key || seenIdeas.has(key)) return false;
          seenIdeas.add(key);
          return true;
        });
      }

      const tagged = serverSessions.map(s => ({ ...s, source: 'cloud' }));
      // Dedup all sessions (same normalized idea = keep newest only)
      const seenServerIdeas = new Set();
      const dedupedSessions = [...tagged, ...localSessions].filter(s => {
        const nodeCount = s.nodeCount || s.rawNodes?.length || 0;
        const ideaText = (s.idea || '').trim();
        const hasIdea = ideaText && ideaText !== 'Untitled session';
        if (!nodeCount && !hasIdea) return false;
        const key = normalizeIdea(s.idea);
        if (key && seenServerIdeas.has(key)) return false;
        if (key) seenServerIdeas.add(key);
        return true;
      });
      const newSessions = dedupedSessions;

      setHasMore(serverSessions.length >= PAGE_SIZE);

      if (before) {
        setSessions(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const unique = newSessions.filter(s => !existingIds.has(s.id));
          return [...prev, ...unique].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        });
      } else {
        setSessions(newSessions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Infinite scroll — observe sentinel element
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && !loading && hasMore && sessions.length > 0) {
          const lastCloud = [...sessions].reverse().find(s => s.source === 'cloud');
          if (lastCloud?.updatedAt) fetchSessions(lastCloud.updatedAt);
        }
      },
      { root: sessionsContainerRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, sessions, fetchSessions]);

  // Expose refresh to parent (resets to initial load)
  const refresh = useCallback(() => { setHasMore(true); fetchSessions(); }, [fetchSessions]);
  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  // Delete handler (two-click pattern)
  const handleDelete = useCallback(async (e, session) => {
    e.stopPropagation();
    if (deleteConfirm !== session.id) {
      setDeleteConfirm(session.id);
      return;
    }
    try {
      if (session.source === 'cloud') {
        await authFetch(`${API_URL}/api/sessions/${session.id}`, { method: 'DELETE' });
      } else {
        for (const key of ['IDEA_CANVAS_SESSIONS', 'CODEBASE_CANVAS_SESSIONS']) {
          const stored = readLocalSessions(key);
          const filtered = stored.filter(s => s.id !== session.id);
          if (filtered.length !== stored.length) {
            localStorage.setItem(key, JSON.stringify(filtered));
          }
        }
      }
      setSessions(prev => prev.filter(s => s.id !== session.id));
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setDeleteConfirm(null);
  }, [deleteConfirm]);

  useEffect(() => {
    if (deleteConfirm) {
      const timer = setTimeout(() => setDeleteConfirm(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [deleteConfirm]);

  // Filtering & Grouping — build tree first, then group only roots by date
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    const q = searchQuery.toLowerCase();
    return sessions.filter(s =>
      (s.idea || '').toLowerCase().includes(q) ||
      (getModeConfig(s.mode).label || '').toLowerCase().includes(q)
    );
  }, [sessions, searchQuery]);

  // Build parent-child map from flat session list
  const { roots, childMap } = useMemo(() => {
    const cMap = new Map();
    const rts = [];
    for (const s of filteredSessions) {
      if (s.parentSessionId) {
        const siblings = cMap.get(s.parentSessionId) || [];
        siblings.push(s);
        cMap.set(s.parentSessionId, siblings);
      } else {
        rts.push(s);
      }
    }
    return { roots: rts, childMap: cMap };
  }, [filteredSessions]);

  const groupedSessions = useMemo(
    () => groupSessionsByDate(roots),
    [roots]
  );

  // Drag-and-drop handlers for nesting
  const handleDragStart = useCallback((e, session) => {
    dragSessionRef.current = session;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', session.id);
  }, []);

  const handleDragOver = useCallback((e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(targetId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(async (e, targetSession) => {
    e.preventDefault();
    setDragOverId(null);
    const dragged = dragSessionRef.current;
    if (!dragged || dragged.id === targetSession.id) return;
    // Don't nest a parent under its own child
    if (targetSession.parentSessionId === dragged.id) return;

    try {
      await authFetch(`${API_URL}/api/sessions/${dragged.id}/parent`, {
        method: 'PATCH',
        body: JSON.stringify({ parentSessionId: targetSession.id }),
      });
      // Update local state
      setSessions(prev => prev.map(s =>
        s.id === dragged.id ? { ...s, parentSessionId: targetSession.id } : s
      ));
      setExpandedSessions(prev => new Set([...prev, targetSession.id]));
    } catch (err) {
      console.error('Reparent failed:', err);
    }
    dragSessionRef.current = null;
  }, []);

  // Drop on the top-level area (un-nest)
  const handleDropRoot = useCallback(async (e) => {
    e.preventDefault();
    setDragOverId(null);
    const dragged = dragSessionRef.current;
    if (!dragged || !dragged.parentSessionId) return;

    try {
      await authFetch(`${API_URL}/api/sessions/${dragged.id}/parent`, {
        method: 'PATCH',
        body: JSON.stringify({ parentSessionId: null }),
      });
      setSessions(prev => prev.map(s =>
        s.id === dragged.id ? { ...s, parentSessionId: null } : s
      ));
    } catch (err) {
      console.error('Un-nest failed:', err);
    }
    dragSessionRef.current = null;
  }, []);

  // ── Render ─────────────────────────────────────────────────────

  return (
    <aside className={`sidebar ${isCollapsed ? 'sidebar--collapsed' : ''}`}>
      {/* Header */}
      <div className="sidebar-header">
        <button className="sidebar-toggle" onClick={onToggleCollapse} title="Toggle sidebar">
          ☰
        </button>
        {!isCollapsed && (
          <button className="sidebar-new-btn" onClick={onNewSession}>
            <span className="sidebar-new-icon">+</span>
            New
          </button>
        )}
      </div>

      {/* Search */}
      {!isCollapsed && sessions.length > 0 && (
        <div className="sidebar-search-wrap">
          <span className="sidebar-search-icon">⌕</span>
          <input
            className="sidebar-search"
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="sidebar-search-clear" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
      )}

      {/* Session list */}
      <div
        className="sidebar-sessions"
        ref={sessionsContainerRef}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
        onDrop={handleDropRoot}
      >
        {loading ? (
          <div className="sidebar-loading">
            <div className="sl-loading-spinner" />
          </div>
        ) : !isCollapsed && groupedSessions.length > 0 ? (
          groupedSessions.map(group => (
            <div className="sidebar-group" key={group.label}>
              <div className="sidebar-group-label">{group.label}</div>
              {group.sessions.map(session => {
                const mode = getModeConfig(session.mode);
                const isActive = activeSessionId === session.id;
                const isConfirming = deleteConfirm === session.id;
                const isForest = !!session.forestCanvases?.length;
                const isForestExpanded = expandedForests.has(session.id);
                const children = childMap.get(session.id) || [];
                const hasChildren = children.length > 0;
                const isSessionExpanded = expandedSessions.has(session.id);
                const isExpandable = hasChildren || isForest;
                const isAnyExpanded = (hasChildren && isSessionExpanded) || (isForest && isForestExpanded);
                const isDragTarget = dragOverId === session.id;

                return (
                  <React.Fragment key={session.id}>
                    <div
                      className={`sidebar-row ${isActive ? 'sidebar-row--active' : ''} ${isForest ? 'sidebar-row--forest' : ''} ${hasChildren ? 'sidebar-row--parent' : ''} ${isDragTarget ? 'sidebar-row--drop-target' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, session)}
                      onDragOver={(e) => handleDragOver(e, session.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => { e.stopPropagation(); handleDrop(e, session); }}
                      onClick={() => {
                        if (isForest && onOpenForest) {
                          onOpenForest({ id: session.forestId, sessionId: session.id, idea: session.idea, plan: session.forestPlan, canvases: session.forestCanvases });
                        } else {
                          onOpenSession(session);
                        }
                      }}
                    >
                      {isExpandable ? (
                        <button
                          className="sidebar-row-expand"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasChildren) {
                              setExpandedSessions(prev => {
                                const next = new Set(prev);
                                if (next.has(session.id)) next.delete(session.id);
                                else next.add(session.id);
                                return next;
                              });
                            }
                            if (isForest) {
                              setExpandedForests(prev => {
                                const next = new Set(prev);
                                if (next.has(session.id)) next.delete(session.id);
                                else next.add(session.id);
                                return next;
                              });
                            }
                          }}
                        >
                          {isAnyExpanded ? '▾' : '▸'}
                        </button>
                      ) : (
                        <span className="sidebar-row-expand-spacer" />
                      )}
                      <span className="sidebar-row-icon" style={{ color: isForest ? '#6c63ff' : mode.color }}>
                        {isForest ? '📁' : mode.icon}
                      </span>
                      <span className="sidebar-row-title">
                        {session.idea || 'Untitled session'}
                      </span>
                      {hasChildren && (
                        <span className="sidebar-child-count">({children.length})</span>
                      )}
                      <button
                        className={`sidebar-row-delete ${isConfirming ? 'confirming' : ''}`}
                        onClick={(e) => handleDelete(e, session)}
                        title={isConfirming ? 'Click again to confirm' : 'Delete'}
                      >
                        {isConfirming ? '?' : '✕'}
                      </button>
                    </div>
                    {/* Child sessions */}
                    {hasChildren && isSessionExpanded && children.map((child, ci) => {
                      const childMode = getModeConfig(child.mode);
                      const isChildActive = activeSessionId === child.id;
                      const isChildConfirming = deleteConfirm === child.id;
                      const isLast = ci === children.length - 1;
                      return (
                        <div
                          key={child.id}
                          className={`sidebar-row sidebar-row--child ${isChildActive ? 'sidebar-row--active' : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, child)}
                          onClick={() => onOpenSession(child)}
                        >
                          <span className="sidebar-tree-connector">{isLast ? '└' : '├'}</span>
                          <span className="sidebar-row-icon" style={{ color: childMode.color }}>
                            {childMode.icon}
                          </span>
                          <span className="sidebar-row-title">
                            {child.idea || 'Untitled session'}
                          </span>
                          <button
                            className={`sidebar-row-delete ${isChildConfirming ? 'confirming' : ''}`}
                            onClick={(e) => handleDelete(e, child)}
                            title={isChildConfirming ? 'Click again to confirm' : 'Delete'}
                          >
                            {isChildConfirming ? '?' : '✕'}
                          </button>
                        </div>
                      );
                    })}
                    {/* Forest sub-canvases (directory listing) */}
                    {isForest && isForestExpanded && session.forestCanvases.map(canvas => (
                      <div
                        key={canvas.canvasKey}
                        className="sidebar-row sidebar-row--sub"
                        onClick={() => {
                          if (onOpenForest) {
                            onOpenForest({ id: session.forestId, sessionId: session.id, idea: session.idea, plan: session.forestPlan, canvases: session.forestCanvases, activeCanvasKey: canvas.canvasKey });
                          }
                        }}
                      >
                        <span className="sidebar-sub-indent" />
                        <span className={`sidebar-sub-status sidebar-sub-status--${canvas.status || 'pending'}`}>
                          {canvas.status === 'ready' ? '●' : canvas.status === 'generating' ? '◌' : '○'}
                        </span>
                        <span className="sidebar-row-title">{canvas.title}</span>
                        {canvas.nodeCount > 0 && <span className="sidebar-sub-count">{canvas.nodeCount}</span>}
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          ))
        ) : !isCollapsed && sessions.length === 0 && !loading ? (
          <div className="sidebar-empty">No sessions yet</div>
        ) : !isCollapsed ? (
          <div className="sidebar-no-results">No matches</div>
        ) : null}
        {/* Infinite scroll sentinel */}
        {!isCollapsed && hasMore && sessions.length > 0 && (
          <div ref={sentinelRef} className="sidebar-load-more">
            {loadingMore && <div className="sl-loading-spinner" style={{ width: 16, height: 16 }} />}
          </div>
        )}
      </div>

      {/* Footer: user info */}
      {!isCollapsed && authUser && (
        <div className="sidebar-footer">
          <div className="sidebar-user">
            {authUser.photoURL ? (
              <img src={authUser.photoURL} alt="" className="sidebar-user-avatar" referrerPolicy="no-referrer" />
            ) : (
              <div className="sidebar-user-avatar-fallback">
                {(authUser.displayName || authUser.email || '?')[0].toUpperCase()}
              </div>
            )}
            <span className="sidebar-user-name">
              {authUser.displayName || authUser.email || 'User'}
            </span>
          </div>
          <div className="sidebar-footer-actions">
            <button className="sidebar-settings-btn" onClick={onOpenKnowledge} title="Knowledge graph">
              ⬡ Knowledge
            </button>
            <button className="sidebar-settings-btn" onClick={onOpenSettings} title="Settings">
              ⚙ Settings
            </button>
            <button className="sidebar-signout" onClick={authLogout} title="Sign out">
              Sign out
            </button>
          </div>
        </div>
      )}
    </aside>
  );
});

export default Sidebar;
